<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '../composables/useAuth';
import {
    getNotifications,
    getUnreadCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
} from '../services/notificationService';
import {
    getNotificationRoute,
} from '../types/notification';
import type { Notification } from '../types/notification';

const { getAccessTokenSilently } = useAuth();
const router = useRouter();

const isOpen = ref(false);
const notifications = ref<Notification[]>([]);
const unreadCount = ref(0);
const loading = ref(false);
const error = ref<string | null>(null);

// Polling interval (30 seconds)
const POLL_INTERVAL = 30000;
let pollTimer: ReturnType<typeof setInterval> | null = null;

const hasUnread = computed(() => unreadCount.value > 0);
const displayCount = computed(() => unreadCount.value > 99 ? '99+' : unreadCount.value.toString());

async function fetchUnreadCount() {
    try {
        unreadCount.value = await getUnreadCount(getAccessTokenSilently);
    } catch (e) {
        console.error('Failed to fetch unread count:', e);
    }
}

async function fetchNotifications() {
    try {
        loading.value = true;
        error.value = null;
        const result = await getNotifications(getAccessTokenSilently, { limit: 10 });
        notifications.value = result.notifications;
    } catch (e) {
        error.value = 'Failed to load notifications';
        console.error('Failed to fetch notifications:', e);
    } finally {
        loading.value = false;
    }
}

async function handleMarkAsRead(notification: Notification) {
    if (notification.is_read) return;

    try {
        await markNotificationAsRead(getAccessTokenSilently, notification.id);
        notification.is_read = true;
        unreadCount.value = Math.max(0, unreadCount.value - 1);
    } catch (e) {
        console.error('Failed to mark as read:', e);
    }
}

async function handleMarkAllAsRead() {
    try {
        await markAllNotificationsAsRead(getAccessTokenSilently);
        notifications.value.forEach(n => n.is_read = true);
        unreadCount.value = 0;
    } catch (e) {
        console.error('Failed to mark all as read:', e);
    }
}

function handleNotificationClick(notification: Notification) {
    handleMarkAsRead(notification);
    const route = getNotificationRoute(notification);
    if (route) {
        router.push(route);
    }
    isOpen.value = false;
}

function toggleDropdown() {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        fetchNotifications();
    }
}

function closeDropdown() {
    isOpen.value = false;
}

function formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
}

// Start polling on mount
onMounted(() => {
    fetchUnreadCount();
    pollTimer = setInterval(fetchUnreadCount, POLL_INTERVAL);
});

// Stop polling on unmount
onUnmounted(() => {
    if (pollTimer) {
        clearInterval(pollTimer);
    }
});

// Close on click outside
function handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.notification-bell-container')) {
        closeDropdown();
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
    <div class="notification-bell-container relative">
        <!-- Bell Button -->
        <button
            @click="toggleDropdown"
            class="relative w-9 h-9 flex items-center justify-center text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-cc-primary rounded-lg transition-colors"
            :aria-label="`Notifications${hasUnread ? ` (${unreadCount} unread)` : ''}`"
        >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>

            <!-- Unread Badge -->
            <span
                v-if="hasUnread"
                class="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center px-1 py-0.5 text-[10px] font-bold leading-none text-white bg-cc-danger rounded-full min-w-[16px] h-4"
            >
                {{ displayCount }}
            </span>
        </button>

        <!-- Dropdown -->
        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
        >
            <div
                v-if="isOpen"
                class="absolute right-0 top-12 w-96 bg-cc-elevated rounded-cc overflow-hidden z-50"
                style="box-shadow: var(--shadow-dropdown)"
            >
                <!-- Header -->
                <div class="p-4 border-b border-cc-border/20 flex items-center justify-between">
                    <h3 class="font-semibold text-cc-text">Notifications</h3>
                    <button
                        v-if="hasUnread"
                        @click="handleMarkAllAsRead"
                        class="text-xs text-cc-primary hover:text-cc-primary-hover font-medium"
                    >
                        Mark all read
                    </button>
                </div>

                <!-- Loading State -->
                <div v-if="loading" class="p-8 text-center">
                    <div class="animate-spin w-5 h-5 border-2 border-cc-primary border-t-transparent rounded-full mx-auto"></div>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="p-4 text-center text-cc-danger text-sm">
                    {{ error }}
                </div>

                <!-- Empty State -->
                <div v-else-if="notifications.length === 0" class="p-8 text-center text-cc-text-tertiary text-sm">
                    No notifications
                </div>

                <!-- Notifications List -->
                <div v-else class="max-h-[500px] overflow-y-auto">
                    <div
                        v-for="notification in notifications"
                        :key="notification.id"
                        @click="handleNotificationClick(notification)"
                        class="p-4 border-b border-cc-border/10 hover:bg-cc-hover cursor-pointer"
                        :class="{ 'bg-cc-primary/5': !notification.is_read }"
                    >
                        <div class="flex items-start gap-3">
                            <!-- Severity Dot -->
                            <div class="w-2 h-2 rounded-full mt-2 flex-shrink-0" :class="!notification.is_read ? 'bg-cc-danger' : 'bg-cc-warning'"></div>

                            <!-- Content -->
                            <div class="flex-1 min-w-0">
                                <p class="text-sm text-cc-text">
                                    {{ notification.title }}
                                </p>
                                <p class="text-xs text-cc-text-tertiary mt-1 line-clamp-2">
                                    {{ notification.message }}
                                </p>
                                <p class="text-xs text-cc-text-tertiary mt-1">
                                    {{ formatTime(notification.created_at) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer -->
                <div class="px-4 py-3 border-t border-cc-border/20 text-center">
                    <router-link
                        to="/advisor/settings?tab=notifications"
                        @click="closeDropdown"
                        class="text-xs text-cc-primary hover:text-cc-primary-hover font-medium"
                    >
                        Notification Settings
                    </router-link>
                </div>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
